"use client";

/**
 * Atomic twc components for the Treatment Recommendation Card.
 *
 * Mirrors the Figma "AI Dynamic Intake" treatment recommendation card design.
 * All color/spacing values use design token CSS variables so hims + hers brands
 * inherit automatically via brands.css overrides.
 *
 * @see packages/tokens/css/theme.css for available tokens
 */

import { twc } from "@health/ui";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

// ---------------------------------------------------------------------------
// Gradient card container
// ---------------------------------------------------------------------------

/**
 * Full-width dark gradient card — the main backdrop of the recommendation.
 * The gradient is treatment-specific and comes from content config, applied
 * via inline style to keep Tailwind's purge-safe static class list intact.
 */
export const RecommendationGradientCard = twc.div`
  relative w-full rounded-2xl overflow-hidden text-white
  flex flex-col p-6 min-h-[300px]
`;

// ---------------------------------------------------------------------------
// Typography atoms
// ---------------------------------------------------------------------------

/** Small pill label above the treatment name — normal case, Figma-spec 14 px */
export const RecommendationLabel = twc.p`
  text-sm tracking-[-0.12px] text-white
`;

/**
 * Two-tone treatment name. "Sex Rx +" renders in white; everything after
 * " + " renders in the treatment-specific accent color from config.
 * Falls back to plain white if no accent is provided.
 */
export function RecommendationTitle({
  children,
  accentColor,
}: {
  children: string;
  accentColor?: string;
}) {
  const plusIdx = children.indexOf(" + ");
  const hasAccent = accentColor && plusIdx !== -1;
  if (!hasAccent) {
    return (
      <h2 className="text-[32px] font-medium leading-[31.5px] tracking-[-2.01px] text-white">
        {children}
      </h2>
    );
  }
  const prefix = children.slice(0, plusIdx + 2); // "Sex Rx +"
  const suffix = children.slice(plusIdx + 3); // "Climax Control"
  return (
    <h2 className="text-[32px] font-medium leading-[31.5px] tracking-[-2.01px] text-white">
      <span className="block">{prefix}</span>
      <span className="block" style={{ color: accentColor }}>
        {suffix}
      </span>
    </h2>
  );
}

// ---------------------------------------------------------------------------
// Benefit bullet row
// ---------------------------------------------------------------------------

/**
 * A single benefit bullet — frosted left-bar accent + benefit text.
 * Uses a before pseudo-element via a wrapper div rather than a CSS custom
 * property so it remains predictable across brands.
 */
export function RecommendationBullet({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-[10.4px]">
      {/* 6 px wide frosted bar — matches Figma w-[6px] bg-white/22 */}
      <div className="shrink-0 w-1.5 self-stretch rounded-full bg-white/[0.22]" />
      <p className="text-[17.5px] font-medium leading-[17.9px] tracking-[-0.8px] text-white">
        {children}
      </p>
    </div>
  );
}

/** Container for the 3 benefit bullets — 25 px gap matches Figma */
export const RecommendationBulletList = twc.div`flex flex-col gap-[25px]`;

// ---------------------------------------------------------------------------
// Product image slot
// ---------------------------------------------------------------------------

/** Overlaid product image composited into the top-right of the card */
export function RecommendationProductImage({ src, alt }: { src: string; alt: string }) {
  return (
    /*
     * Pill stack overflows the right edge of the card (clipped by the parent's
     * overflow-hidden). The image center sits at ~72% of the card width, with
     * ~30% of the image extending beyond the right edge — matching Figma.
     */
    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-[28%] w-[68%] pointer-events-none">
      <img
        src={src}
        alt={alt}
        className="w-full h-auto object-cover"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAQ accordion row
// ---------------------------------------------------------------------------

type RecommendationAccordionRowProps = {
  question: string;
  answer: string;
};

/**
 * Expandable FAQ row — each item renders as its own standalone rounded card,
 * matching the Figma design where each accordion item is a separate bordered
 * tile rather than rows inside a single grouped container.
 */
export function RecommendationAccordionRow({ question, answer }: RecommendationAccordionRowProps) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="rounded-xl border border-border/20 shadow-lg/2 bg-background overflow-hidden px-4">
      <button
        type="button"
        className="w-full flex items-center justify-between py-5 text-left gap-3"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-text-primary leading-snug">{question}</span>
        <span
          className="shrink-0 text-lg font-light text-text-primary leading-none select-none"
          aria-hidden
        >
          {open ? "-" : "+"}
        </span>
      </button>
      {open && <p className="text-sm text-text-secondary leading-relaxed pb-4">{answer}</p>}
    </div>
  );
}

/** Flex-column container for the FAQ rows — each row is its own card */
export const RecommendationFaqSection = twc.div`flex flex-col gap-3`;

// ---------------------------------------------------------------------------
// Disclaimer footnote
// ---------------------------------------------------------------------------

export const RecommendationDisclaimer = twc.p`
  text-xs text-(--color-text-secondary) leading-relaxed
`;

// ---------------------------------------------------------------------------
// CTA buttons
// ---------------------------------------------------------------------------

const ctaVariants = cva(
  "w-full rounded-full py-3.5 px-6 text-sm font-semibold transition-colors text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)",
  {
    variants: {
      $variant: {
        primary: "bg-(--color-accent) text-white hover:bg-(--color-accent-hover)",
        secondary:
          "bg-transparent border border-(--color-chips-border) text-(--color-text-primary) hover:bg-(--color-accent-light)",
      },
    },
    defaultVariants: { $variant: "primary" },
  },
);

type CtaButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof ctaVariants>;

export function RecommendationCtaButton({ $variant, className, ...props }: CtaButtonProps) {
  return <button type="button" className={ctaVariants({ $variant, className })} {...props} />;
}

/** Wrapper for the CTA button pair */
export const RecommendationCtaRow = twc.div`flex flex-col gap-3 w-full`;
